import { useCallback, useState } from 'react';
import type { Partner, Tool } from '../game/partners';
import { allPartners } from '../game/partners';
import { savePartner, saveTool } from '../game/admin';

interface Props {
  /** Editing an existing runtime tool, or null to create one. */
  existing: Tool | null;
  onDone: () => void;
  onCancel: () => void;
}

/** Contrast of an accent against the card it is drawn on. Mirrors the server. */
function accentContrast(hex: string): number {
  const lum = (value: string) => {
    const channels = [1, 3, 5].map((i) => parseInt(value.slice(i, i + 2), 16) / 255);
    const linear = channels.map((c) =>
      c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4,
    );
    return 0.2126 * (linear[0] ?? 0) + 0.7152 * (linear[1] ?? 0) + 0.0722 * (linear[2] ?? 0);
  };
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return 0;
  const card = lum('#152c4d');
  const accent = lum(hex);
  const hi = Math.max(card, accent);
  const lo = Math.min(card, accent);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Create a tool category, and the vendor that makes it if it is new.
 *
 * Both in one screen because they are one thought — "we are adding Bamboo" —
 * even though they are two records. Picking an existing vendor is the common
 * case; a new vendor needs its legal name, which is what the trademark notice
 * cites, and an accent, which is the only thing distinguishing its cards.
 */
export function ToolEditor({ existing, onDone, onCancel }: Props) {
  const partners = allPartners();

  const [toolId, setToolId] = useState(existing?.id ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [short, setShort] = useState(existing?.short ?? '');
  const [blurb, setBlurb] = useState(existing?.blurb ?? '');
  const [targetLevels, setTargetLevels] = useState(String(existing?.targetLevels ?? 10));

  const [partnerMode, setPartnerMode] = useState<'existing' | 'new'>(
    existing ? 'existing' : 'existing',
  );
  const [partnerId, setPartnerId] = useState(existing?.partnerId ?? partners[0]?.id ?? '');
  const [newPartnerName, setNewPartnerName] = useState('');
  const [newPartnerLegal, setNewPartnerLegal] = useState('');
  const [newPartnerAccent, setNewPartnerAccent] = useState('#6ba5ff');

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);

  const contrast = accentContrast(newPartnerAccent);

  const onSave = useCallback(async () => {
    setMessage(null);
    setProblems([]);
    setBusy(true);

    let usePartnerId = partnerId;

    // A new vendor has to exist before the tool that references it, or the
    // tool is rejected for naming an unknown vendor.
    if (partnerMode === 'new') {
      const partner: Partner = {
        id: slugify(newPartnerName),
        name: newPartnerName.trim(),
        legalName: newPartnerLegal.trim(),
        accent: newPartnerAccent.trim(),
      };
      const outcome = await savePartner(partner);
      if (!outcome.ok) {
        setBusy(false);
        setMessage(outcome.message);
        setProblems(outcome.problems);
        return;
      }
      usePartnerId = partner.id;
    }

    const tool: Tool = {
      id: existing?.id ?? slugify(toolId || short || name),
      partnerId: usePartnerId,
      name: name.trim(),
      short: short.trim(),
      blurb: blurb.trim(),
      targetLevels: Number(targetLevels),
    };

    const outcome = await saveTool(tool);
    setBusy(false);
    setMessage(outcome.message);
    setProblems(outcome.problems);
    if (outcome.ok) onDone();
  }, [
    partnerMode, partnerId, newPartnerName, newPartnerLegal, newPartnerAccent,
    existing, toolId, short, name, blurb, targetLevels, onDone,
  ]);

  return (
    <div className="panel">
      <div className="panel__inner">
        <p className="case-eyebrow">{existing ? 'Edit tool' : 'New tool'}</p>
        <h1>{existing ? existing.short : 'Add a tool'}</h1>

        <p className="admin-warning">
          A new tool appears on the splash and the picker <b>immediately</b>. It
          shows as "in preparation" until it has a level, so adding one early is
          safe — but it is public the moment you save.
        </p>

        <h2>The product</h2>
        <label className="field">
          <span className="field__label">Full product name</span>
          <input
            className="field__input"
            value={name}
            placeholder="Atlassian Bamboo"
            onChange={(event) => setName(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Short name — what players call it</span>
          <input
            className="field__input"
            value={short}
            placeholder="Bamboo"
            onChange={(event) => setShort(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">One line for the picker</span>
          <input
            className="field__input"
            value={blurb}
            placeholder="Build plans, agents and deployment projects."
            onChange={(event) => setBlurb(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">How many cases the finished set will have</span>
          <input
            className="field__input"
            type="number"
            min={1}
            max={100}
            value={targetLevels}
            onChange={(event) => setTargetLevels(event.target.value)}
          />
        </label>

        {!existing && (
          <label className="field">
            <span className="field__label">Id (leave blank to derive from the short name)</span>
            <input
              className="field__input"
              value={toolId}
              placeholder="bamboo"
              onChange={(event) => setToolId(event.target.value)}
            />
          </label>
        )}

        <h2>Who makes it</h2>
        <div className="admin-arrow__head">
          <label className="admin-arrow__toggle">
            <input
              type="radio"
              name="partner-mode"
              checked={partnerMode === 'existing'}
              onChange={() => setPartnerMode('existing')}
            />
            An existing vendor
          </label>
          <label className="admin-arrow__toggle">
            <input
              type="radio"
              name="partner-mode"
              checked={partnerMode === 'new'}
              onChange={() => setPartnerMode('new')}
            />
            A new vendor
          </label>
        </div>

        {partnerMode === 'existing' ? (
          <label className="field">
            <span className="field__label">Vendor</span>
            <select
              className="field__input"
              value={partnerId}
              onChange={(event) => setPartnerId(event.target.value)}
            >
              {partners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <>
            <label className="field">
              <span className="field__label">Vendor name</span>
              <input
                className="field__input"
                value={newPartnerName}
                placeholder="Atlassian"
                onChange={(event) => setNewPartnerName(event.target.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">
                Legal name — this is what the trademark notice cites
              </span>
              <input
                className="field__input"
                value={newPartnerLegal}
                placeholder="Atlassian Pty Ltd"
                onChange={(event) => setNewPartnerLegal(event.target.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">Accent colour</span>
              <span className="admin-accent">
                <input
                  type="color"
                  value={newPartnerAccent}
                  onChange={(event) => setNewPartnerAccent(event.target.value)}
                />
                <input
                  className="field__input"
                  value={newPartnerAccent}
                  onChange={(event) => setNewPartnerAccent(event.target.value)}
                />
              </span>
              <span
                className={`fineprint${contrast < 4.5 ? ' admin-contrast-fail' : ''}`}
              >
                {contrast
                  ? `${contrast.toFixed(2)}:1 against the card background — ${
                      contrast >= 4.5 ? 'passes WCAG AA' : 'too low, AA needs 4.5:1'
                    }`
                  : 'Enter a hex colour like #6ba5ff'}
              </span>
            </label>
            <p className="fineprint">
              This is <b>our</b> colour for this vendor's cards, chosen to be
              readable on a dark background. It is not their brand asset and must
              not be presented as one — the game uses no vendor logos.
            </p>
          </>
        )}

        {problems.length > 0 && (
          <div className="form__message" role="alert">
            <b>Not saved:</b>
            <ul className="admin-problems">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </div>
        )}
        {message && problems.length === 0 && (
          <p className="form__message" role="status">
            {message}
          </p>
        )}

        <div className="sheet__actions" style={{ marginTop: 20 }}>
          <button type="button" className="btn" disabled={busy} onClick={() => void onSave()}>
            {busy ? 'Saving…' : 'Save — goes live now'}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}
